/**
 * =============================================================
 * AADHAV ACADEMY – ADMIN STUDENT FEE PLAN API
 * Role: Admin
 * Scope: Student-to-fee plan mapping and history management
 * Standard: Enterprise / MNC Level
 * =============================================================
 */

import { apiCall } from "../core/apiCall";

const STUDENT_FEE_PLAN_ENDPOINT = "/admin/fees/student_fee_plan.php";

/**
 * GET – Fetch Student's Current Active Fee Plan
 * 
 * @param {string|number} studentId - The unique identifier of the student
 * @param {Object} [options={}] - Additional API options/config
 * @returns {Promise<{success: boolean, message?: string, data: Object|null}>}
 */
export const getStudentActiveFeePlan = async (studentId, options = {}) => {
  if (!studentId) {
    return { success: false, message: "Invalid student ID provided", data: null };
  }

  try {
    const res = await apiCall(
      "GET",
      STUDENT_FEE_PLAN_ENDPOINT,
      null,
      "Admin – Fetch Active Fee Plan",
      {
        params: { student_id: studentId },
        ...options,
      }
    );

    if (!res?.status) {
      return {
        success: false,
        message: res?.message || "Failed to fetch active fee plan",
        data: null,
      };
    }

    return {
      success: true,
      data: res.data || null,
    };
  } catch (error) {
    console.error("Admin Fetch Active Fee Plan Error:", error);
    return {
      success: false,
      message: error.message || "An unexpected error occurred while fetching the active fee plan",
      data: null,
    };
  }
};

/**
 * GET – Fetch Student Fee Plan History Audit Trail
 * 
 * @param {string|number} studentId - The unique identifier of the student
 * @param {Object} [options={}] - Additional API options/config
 * @returns {Promise<{success: boolean, message?: string, data: Array}>}
 */
export const getStudentFeePlanHistory = async (studentId, options = {}) => {
  if (!studentId) {
    return { success: false, message: "Invalid student ID provided", data: [] };
  }

  try {
    const res = await apiCall(
      "GET",
      STUDENT_FEE_PLAN_ENDPOINT,
      null,
      "Admin – Fetch Fee Plan History",
      {
        params: { student_id: studentId, action: "history" },
        ...options,
      }
    );

    if (!res?.status) {
      return {
        success: false,
        message: res?.message || "Failed to fetch fee plan history",
        data: [],
      };
    }

    return {
      success: true,
      data: res.data || [],
    };
  } catch (error) {
    console.error("Admin Fetch Fee Plan History Error:", error);
    return {
      success: false,
      message: error.message || "An unexpected error occurred while fetching fee plan history",
      data: [],
    };
  }
};

/**
 * POST – Assign or Create Student Fee Plan (Auto-Archives Previous)
 * 
 * @param {Object} payload - The fee plan creation data payload
 * @param {Object} [options={}] - Additional API options/config
 * @returns {Promise<{success: boolean, message: string, data?: Object|null}>}
 */
export const createStudentFeePlan = async (payload, options = {}) => {
  if (!payload || !payload.student_id) {
    return { success: false, message: "Invalid payload or missing student ID" };
  }

  try {
    const res = await apiCall(
      "POST",
      STUDENT_FEE_PLAN_ENDPOINT,
      payload,
      "Admin – Create/Map Fee Plan",
      options
    );

    if (!res?.status) {
      return {
        success: false,
        message: res?.message || "Failed to map fee plan",
      };
    }

    return {
      success: true,
      data: res.data || null,
      message: res.message || "Fee plan mapped successfully",
    };
  } catch (error) {
    console.error("Admin Create Fee Plan Error:", error);
    return {
      success: false,
      message: error.message || "An unexpected error occurred while mapping the fee plan",
    };
  }
};

/**
 * PUT – Update Student Fee Plan Record (Supports Correction of Historical/Active Entries)
 * 
 * @param {string|number} planId - The unique identifier of the fee plan/record
 * @param {Object} payload - The updated fee attributes (amount, cycle, effective_from, etc.)
 * @param {Object} [options={}] - Additional API options/config
 * @returns {Promise<{success: boolean, message: string, data?: Object|null}>}
 */
export const updateStudentFeePlan = async (planId, payload, options = {}) => {
  if (!planId) {
    return { success: false, message: "Invalid plan or record ID provided" };
  }

  try {
    const res = await apiCall(
      "PUT",
      `${STUDENT_FEE_PLAN_ENDPOINT}?id=${planId}`,
      payload,
      "Admin – Update Fee Plan",
      options
    );

    if (!res?.status) {
      return {
        success: false,
        message: res?.message || "Failed to update fee plan record",
      };
    }

    return {
      success: true,
      data: res.data || null,
      message: res.message || "Fee plan record updated successfully",
    };
  } catch (error) {
    console.error("Admin Update Fee Plan Error:", error);
    return {
      success: false,
      message: error.message || "An unexpected error occurred while updating the fee plan",
    };
  }
};

/**
 * DELETE – Deactivate Student Fee Plan
 * 
 * @param {string|number} planId - The unique identifier of the fee plan/record
 * @param {Object} [options={}] - Additional API options/config
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const deactivateStudentFeePlan = async (planId, options = {}) => {
  if (!planId) {
    return { success: false, message: "Invalid plan ID provided" };
  }

  try {
    const res = await apiCall(
      "DELETE",
      `${STUDENT_FEE_PLAN_ENDPOINT}?id=${planId}`,
      null,
      "Admin – Deactivate Fee Plan",
      options
    );

    if (!res?.status) {
      return {
        success: false,
        message: res?.message || "Failed to deactivate fee plan",
      };
    }

    return {
      success: true,
      message: res.message || "Fee plan deactivated successfully",
    };
  } catch (error) {
    console.error("Admin Deactivate Fee Plan Error:", error);
    return {
      success: false,
      message: error.message || "An unexpected error occurred while deactivating the fee plan",
    };
  }
};